"use client";

import type { ReactNode } from "react";
import { Container, Section, SectionHeader } from "@/components/ui/Container";
import { ModuleGrid } from "@/components/modules/ModuleCard";
import { EventHero } from "@/components/modules/EventHero";
import { EventStartCard } from "@/components/modules/EventStartCard";
import { EventView } from "@/components/modules/EventView";
import { isPast } from "@/components/modules/cards/CardShell";
import { firstRecordingId } from "@/components/modules/eventRecordings";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import type { ModuleDetail } from "@/lib/lecturehub/types";
import { isFreeToUnlock } from "./paywall";

export function EventDetailClient({
  topSlot,
  sideSlot,
  lang,
  moduleId,
  module: m,
  enrolmentIds,
  isAuthed,
  initialSaved = false,
  isSubscription,
  library = false,
  cfHash = null,
}: {
  /** The template's `content_top` placement — rendered at the top of the content column. */
  topSlot?: ReactNode;
  /** The template's `content_right` placement — rendered in the card column, under the card. */
  sideSlot?: ReactNode;
  lang: Lang;
  moduleId: number;
  module: ModuleDetail;
  enrolmentIds: string[];
  isAuthed: boolean;
  /** Server-resolved bookmark state (loadSavedIds) — the schema has no per-module `saved` field. */
  initialSaved?: boolean;
  /** Portal runs in subscription mode (`portal.paymentModel`) — decides where a
   *  failed enrolment sends the user. Pass `portal.isSubscription`. */
  isSubscription: boolean;
  library?: boolean;
  /** Portal.cloudflareAccountHash — threads down to hero/poster image URLs. */
  cfHash?: string | null;
}) {
  const t = useDict();
  // The marketing header overlays the dark hero banner only for guests on the
  // public surface (authed users + /library render under the solid AppShell).
  const overlayHeader = !isAuthed && !library;
  const enrolledIds = new Set(enrolmentIds);
  const enrolled = enrolledIds.has(String(m.id));
  const past = isPast(m.event?.dateTo);
  const live = m.event?.eventType === "live";

  // Recordings are watchable only on the in-app /library surface and only once
  // enrolled — the player route lives under /library and gates on enrolment.
  // On the public marketing page (`library === false`) we never link in; the
  // Register CTA handles it.
  const watchBase = library ? langPath(lang, `/library/events/${moduleId}/${m.slug ?? ""}`) : null;
  const firstRecId = firstRecordingId(m.event);
  const canWatch = !!watchBase && enrolled;
  const watchHref = canWatch && firstRecId ? `${watchBase}/${firstRecId}` : null;

  // `m.refType` is `event` here by construction: the route resolver
  // (lib/routes/resolve-detail.ts) 308s a wrong-type id to the route that
  // owns it before this renders, on the public and the /library surface.
  // A "not my type" fallback used to live here; it was unreachable.
  return (
    <>
      <EventHero
        lang={lang}
        module={m}
        library={library}
        overlayHeader={overlayHeader}
        cfHash={cfHash}
      />
      <Section className="!pt-5 !pb-12 animate-in fade-in duration-500 [animation-timing-function:var(--ease-smooth)]">
        <Container className="grid lg:grid-cols-[1fr_360px] gap-6 lg:gap-8 items-start">
          {/* Abstract is rendered inside EventView's "About event" SectionCard
              as the editorial deck (matches CourseView/CourseDetailClient
              split — they belong to one block, not two floating elements). */}
          <div className="min-w-0">
            {topSlot}
            <EventView
              module={m}
              lang={lang}
              isAuthed={isAuthed}
              watchBase={canWatch ? watchBase : null}
            />
          </div>

          <div className="grid gap-4 lg:sticky lg:top-[calc(var(--marketing-header-h)+1rem)]">
            <EventStartCard
              lang={lang}
              moduleId={moduleId}
              event={m.event}
              free={isFreeToUnlock(m.price)}
              enrolled={enrolled}
              isPast={past}
              isLive={live}
              isAuthed={isAuthed}
              isSubscription={isSubscription}
              initialLiked={m.liked === true}
              initialSaved={initialSaved}
              watchHref={watchHref}
              showSave={false}
            />
            {sideSlot}
          </div>
        </Container>
      </Section>

      {m.similarModules?.length ? (
        <Section className="bg-muted/40">
          <Container>
            <SectionHeader title={t.detail.similar} />
            <ModuleGrid
              modules={m.similarModules.slice(0, 6)}
              lang={lang}
              t={t}
              enrolledIds={enrolledIds}
              library={library}
            />
          </Container>
        </Section>
      ) : null}
    </>
  );
}
